import type React from "react";
import {
  TableRow,
  TableCell,
  Box,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import type { TFunction } from "i18next";
import type { TruckHistory } from "../../../types/truckHistory";
import type { TranslationKeys } from "./utils";
import { formatDate, getStatusColor } from "./utils";
import UserAvatar from "../../common/UserAvatar";

export interface HistoryEntryRowProps {
  entry: TruckHistory;
  getCityName: (cityId: string) => string;
  onDelete: (id: string) => void;
  canEdit: (entry: TruckHistory) => boolean;
  isDeleting: boolean;
  isExpanded?: boolean;
  i18n: any;
  t: TFunction;
}

const HistoryEntryRow: React.FC<HistoryEntryRowProps> = ({
  entry,
  getCityName,
  onDelete,
  canEdit,
  isDeleting,
  isExpanded,
  i18n,
  t,
}) => {
  const isDeleted = entry.isDeleted;
  const opacity = isExpanded ? 0.7 : 1;

  return (
    <TableRow
      sx={{
        "& td": {
          py: 0.5,
          color: "text.secondary",
          fontSize: "0.875rem",
          borderBottom: "none",
          backgroundColor: "transparent",
        },
        "& .MuiBox-root": {
          backgroundColor: "transparent",
        },
      }}
    >
      <TableCell width="15%">{formatDate(entry.timestamp, i18n.language)}</TableCell>
      <TableCell width="120px">
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Chip
            label={t((entry.status || "UNKNOWN") as TranslationKeys)}
            color={getStatusColor(entry.status)}
            size="small"
            sx={{ opacity }}
          />
        </Box>
      </TableCell>
      <TableCell width="20%">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">
            {entry.location ? getCityName(entry.location.cityId) : "-"}
          </Typography>
        </Box>
      </TableCell>
      <TableCell width="20%">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">
            {entry.destination ? getCityName(entry.destination.cityId) : "-"}
          </Typography>
        </Box>
      </TableCell>
      <TableCell width="20%">{entry.notes || "-"}</TableCell>
      <TableCell width="10%">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar
            user={entry.user}
            size={24}
            tooltipText={t("Created by {{name}} on {{date}}" as TranslationKeys, {
              name: entry.user.name,
              date: entry.createdAt ? formatDate(entry.createdAt, i18n.language) : "-",
            })}
          />
        </Box>
      </TableCell>
      <TableCell width="5%">
        {isDeleted ? (
          <Box
            sx={{
              opacity: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UserAvatar
              user={entry.deletedBy || { _id: "-", name: "-" }}
              size={24}
              tooltipText={t("Deleted by {{name}} on {{date}}" as TranslationKeys, {
                name: entry.deletedBy?.name || "-",
                date: entry.deletedAt ? formatDate(entry.deletedAt, i18n.language) : "-",
              })}
            />
          </Box>
        ) : (
          <IconButton
            size="small"
            onClick={() => onDelete(entry._id)}
            disabled={isDeleting || !canEdit(entry)}
            sx={{
              opacity: 0.7,
              padding: "2px",
              color: "error.main",
              "&:hover": {
                opacity: 1,
                backgroundColor: "error.light",
                color: "error.contrastText",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1rem",
              },
            }}
          >
            {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default HistoryEntryRow;
